import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../App';
import { datumUndZeit } from '../lib/zeit';

// A-1: Essen-Abschlüsse ohne Streckennachweis dürfen nicht still bleiben. Die Liste zeigt
// zuerst Fahrten, die ohne Abrechnung beendet wurden („Preis in Prüfung“), danach Freigaben zum
// gebuchten Schätzpreis (abgerechnet erst, wenn die Fahrt abgeschlossen ist). Ein Klick öffnet
// das Fahrt-Detail. Owner 18.10.2026 „Admin-Werkzeug“: eine Prüfung ohne Geld schließt der Admin
// hier genau einmal ab — Betrag (höchstens der gebuchte Fahrpreis) über die gebuchte Zahlart oder
// ohne Abrechnung mit Freigabe der Reserve.
const euro = cents => (cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const LABEL = { unsettled: 'Preis in Prüfung', estimate: 'Schätzpreis, Strecke nicht nachgewiesen' };
// Eine Freigabe zum Schätzpreis ist erst abgerechnet, wenn die Fahrt abgeschlossen ist.
const label = review => (review.mode === 'estimate' && review.ride_status !== 'completed'
  ? 'Schätzpreis freigegeben, noch nicht abgeschlossen' : LABEL[review.mode] || review.mode);
const settled = review => review.mode === 'estimate' && review.ride_status === 'completed';
const newRequestId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID()
  : `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
const ERRORS = {
  essen_review_amount_invalid: 'Betrag ungültig: höchstens der gebuchte Fahrpreis.',
  essen_review_cash_confirmation_required: 'Bitte bestätigen, dass der Fahrer den Betrag bar kassiert hat.',
  essen_review_card_reserve_unavailable: 'Die Reserve ist nicht mehr einziehbar (abgelaufen oder zu klein). Bitte ohne Abrechnung abschließen.',
  essen_review_already_resolved: 'Diese Prüfung ist bereits abgeschlossen.',
  essen_review_payment_already_captured: 'Bereits eingezogen – Zuordnung prüfen. Keine Freigabe, keine automatische Erstattung.',
  essen_review_not_open: 'Diese Prüfung ist nicht mehr offen.',
  essen_review_reason_required: 'Bitte einen Grund angeben (3–300 Zeichen).',
  essen_review_booked_amount_unknown: 'Der gebuchte Betrag ist nicht belegt. Bitte ohne Abrechnung abschließen.',
  essen_review_booked_binding: 'Der gebuchte Betrag passt nicht mehr zur Fahrt. Bitte ohne Abrechnung abschließen.',
  essen_review_wallet_unsupported: 'Fahrten mit Guthaben werden hier nicht abgerechnet. Bitte ohne Abrechnung abschließen.',
  essen_review_reservation_unsupported: 'Reservierungen werden hier nicht abgerechnet. Bitte ohne Abrechnung abschließen.',
  essen_review_deduction_unsupported: 'Fahrten mit Aktionsguthaben oder Abzügen werden hier nicht abgerechnet. Bitte ohne Abrechnung abschließen.',
  essen_review_price_semantics_unsupported: 'Diese Fahrt hat ein altes Preisformat. Bitte ohne Abrechnung abschließen.',
  essen_review_paypal_tip_unsupported: 'Ein bestätigtes PayPal-Trinkgeld wird hier nicht abgerechnet. Bitte ohne Abrechnung abschließen.',
  essen_review_payment_method_unsupported: 'Diese Zahlart wird hier nicht abgerechnet. Bitte ohne Abrechnung abschließen.',
};

function ResolveForm({ review, onDone }) {
  // A1-MISCHFAHRT: Bei einer Fahrt mit Zieländerung ist der gebuchte Betrag nur die Obergrenze (auch nicht
  // angefahrene Stopps) — kein vorausgefüllter Betrag, der Admin trägt ihn bewusst ein.
  const [amount, setAmount] = useState(review.booked_fare_cents != null && !review.mixed ? (review.booked_fare_cents / 100).toFixed(2).replace('.', ',') : '');
  const [reason, setReason] = useState('');
  const [cash, setCash] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const requestId = useRef(newRequestId());
  const fareCents = Math.round(Number(String(amount).replace(',', '.')) * 100);
  const cashRide = review.payment_method === 'cash';
  const validAmount = Number.isSafeInteger(fareCents) && fareCents > 0
    && (review.booked_fare_cents == null || fareCents <= review.booked_fare_cents);
  async function submit(kind) {
    if (busy) return;
    setBusy(true); setError('');
    try {
      if (kind === 'settle') {
        await api.post(`/rides/${encodeURIComponent(review.ride_id)}/admin-essen-review-settle`, {
          request_id: requestId.current, fare_cents: fareCents, reason: reason.trim(), ...(cashRide ? { cash_received: cash } : {}) });
      } else {
        await api.post(`/rides/${encodeURIComponent(review.ride_id)}/admin-essen-review-release`, {
          request_id: requestId.current, reason: reason.trim() });
      }
      onDone?.();
    } catch (e) {
      const code = e?.response?.data?.error;
      setError(ERRORS[code] || `Nicht gespeichert (${code || 'Antwort unklar'}). Prüfliste neu laden.`);
      // Eine neue Absicht bekommt eine neue Kennung; eine gespeicherte Wirkung bleibt idempotent.
      if (code && code !== 'essen_review_already_resolved') requestId.current = newRequestId();
    } finally { setBusy(false); }
  }
  return (
    <div className="rides-essen-resolve">
      <label>Fahrpreis in € (höchstens {review.booked_fare_cents != null ? euro(review.booked_fare_cents) : 'der gebuchte'})
        <input inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} />
      </label>
      <label>Grund
        <input value={reason} maxLength={300} onChange={e => setReason(e.target.value)} placeholder="z. B. Strecke laut Fahrer bestätigt" />
      </label>
      <small>Bitte keine personenbezogenen Daten (Namen, Telefonnummern) eintragen.</small>
      {cashRide && (
        <label className="rides-essen-resolve-check">
          <input type="checkbox" checked={cash} onChange={e => setCash(e.target.checked)} /> Fahrer hat den Kundenbetrag bar kassiert (Fahrpreis abzüglich gebuchter Aktion)
        </label>
      )}
      <div>
        <button type="button" disabled={busy || !validAmount || reason.trim().length < 3 || (cashRide && !cash)} onClick={() => submit('settle')}>
          {busy ? 'Wird gespeichert …' : 'Abrechnen'}
        </button>
        <button type="button" disabled={busy || reason.trim().length < 3} onClick={() => submit('release')}>
          {cashRide ? 'Ohne Abrechnung abschließen' : 'Ohne Abrechnung abschließen, Reserve freigeben'}
        </button>
      </div>
      {error && <p role="alert">{error}</p>}
    </div>
  );
}

export default function EssenCompletionReviews({ onOpen, reloadKey }) {
  const { can } = useAuth();
  const allowed = can('finance.manage');
  const [reviews, setReviews] = useState([]);
  const [resolving, setResolving] = useState(null);
  const [tick, setTick] = useState(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (!allowed) return;
    api.get('/rides/admin-essen-completion-reviews')
      .then(({ data }) => { if (mounted.current) setReviews(Array.isArray(data?.reviews) ? data.reviews : []); })
      .catch(() => { /* Ohne Liste bleibt die Fahrtenliste wie bisher; das Detail zeigt den Prüfstand. */ });
  }, [allowed, reloadKey, tick]);
  if (!allowed || !reviews.length) return null;
  const open = reviews.filter(review => review.mode === 'unsettled').length;
  const estimated = reviews.filter(settled).length;
  return (
    <section className="rides-essen-reviews" aria-label="Essen-Abschlüsse ohne Streckennachweis">
      <header>
        <strong>Essen-Abschlüsse ohne Streckennachweis</strong>
        <span>{open} Preis in Prüfung · {estimated} zum Schätzpreis abgerechnet{reviews.length - open - estimated ? ` · ${reviews.length - open - estimated} freigegeben, offen` : ''}</span>
      </header>
      <ul>
        {reviews.map(review => (
          <li key={`${review.ride_id}:${review.mode}`}>
            <button type="button" onClick={() => onOpen?.(review.ride_id)}>
              <b>{label(review)}</b>
              {review.mixed && <span>Fahrt mit Zieländerung</span>}
              <span>Fahrt {review.ride_id.slice(0, 8)}…</span>
              <span>Fahrer: {review.driver_name || (review.driver_id ? `${review.driver_id.slice(0, 8)}…` : '—')}</span>
              <span>{review.payment_method === 'cash' ? 'Bar' : 'Karte'}</span>
              <span>Grund: {review.reason}</span>
              <span>Ende: {datumUndZeit(review.ended_at, { jahr: 'voll' })}</span>
              {review.estimate_cents != null && (
                <span>{settled(review) ? 'Abgerechnet' : review.mixed ? 'Gebuchter Betrag (Obergrenze)' : 'Angebotsschätzung'} {euro(review.estimate_cents)}</span>
              )}
              {review.mixed && review.driver_review_ends_30d > 1 && (
                <span>Fahrer hat {review.driver_review_ends_30d} Fahrten in 30 Tagen ohne Abrechnung beendet</span>
              )}
              {review.mode === 'unsettled' && review.reserve_expires_at && (
                <span>Reserve verfällt am {datumUndZeit(review.reserve_expires_at, { jahr: 'voll' })}</span>
              )}
            </button>
            {review.mode === 'unsettled' && (resolving === review.ride_id
              ? <ResolveForm review={review} onDone={() => { setResolving(null); setTick(t => t + 1); }} />
              : <button type="button" className="rides-essen-resolve-open" onClick={() => setResolving(review.ride_id)}>Prüfung abschließen</button>)}
          </li>
        ))}
      </ul>
    </section>
  );
}
